import { UploadOutlined } from '@ant-design/icons';
import { Form, Modal, Typography, Upload, UploadFile } from 'antd';
import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';

import bytes from 'bytes';
import { UploadFileClass } from './../../classes/UploadFileClass';
import { JUploadProps, UploadEvent } from './../../models';
import { isImage, urlToObject } from './../../utils';
import { useCompositorContext } from './../CompositorProvider';
import { FormItem } from './../FormItem';
import { JCropImage } from './JCropImage';

import type { RcFile } from 'antd/es/upload';
import { TRANSLATION_CODES } from '../../constants';

const getBase64 = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });
export interface ValidateObject {
    validateStatus: "" | "error" | "success" | "warning" | "validating" | undefined,
    help: React.ReactNode[]
}
export const JUpload: FC<JUploadProps> = props => {
    const formInstance = Form.useFormInstance()
    const { translate, getURLFromFileRef } = useCompositorContext();

    const [fileList, setFileList] = useState<UploadFile[]>()
    const [initialValue, setInitialValue] = useState<UploadFile[]>()

    const [cropImage, setCropImage] = useState<UploadFile | null>(null)

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    const [sizeError, setSizeError] = useState<string[] | undefined>()

    const maxSize = useMemo(() => props.maxSize || 0, [props.maxSize])
    const sizeLabel = useMemo(() => bytes(maxSize), [maxSize])
    const sizeMessage = useMemo(() => translate(TRANSLATION_CODES.MAX_SIZE, { size: sizeLabel }), [sizeLabel])

    useEffect(() => {
        (async () => {
            const _fileList = await getDefaultValues()
            if (_fileList?.length) {
                setFileList(_fileList)
                setInitialValue(_fileList)
            }

        })()
    }, [props.value])

    useEffect(() => {
        formInstance.setFieldValue(props.fullname, fileList)

        if (props.onChange) {
            props.onChange(fileList)
        }
    }, [fileList])

    function getNameFromPath(path: string) {
        const filename = path.replace(/^.*[\\\/]/, '')
        const matchResult = filename.match(/([A-Za-z][^\/]*?)\./)
        if (matchResult?.length) {
            return atob(matchResult[1]).replace(/\x00/g, "")
        }
        return filename
    }
    async function getDefaultValues() {
        const files: UploadFile[] = []
        if (props.value?.length) {
            for (const value of props.value) {
                if (value) {
                    try {

                        const fileurl = await getURLFromFileRef(value);
                        const filename = getNameFromPath(value);
                        const file = await urlToObject(fileurl, filename);

                        const uploadFile = new UploadFileClass(file, filename, 100, fileurl)

                        files.push(uploadFile)
                    } catch (error) {
                        console.error(error)
                    }

                }
            }
        }
        return files
    }

    const onChange = (event: UploadEvent) => {
        if (props.enableCrop) {
            const lastUploadedFile = event.fileList[event.fileList.length - 1]
            if (isImage(lastUploadedFile)) {
                openCrop(lastUploadedFile)
            }
        } else {
            setFileList(event.fileList.slice().map(file => {
                file.status = 'done'
                return file
            }))
        }
    }

    const onSaveCrop = (file: UploadFile) => {
        props.onAction({
            type: 'OPEN_CROP'
        })
        closeCrop()
        setFileList(prev => {
            if (Array.isArray(prev) && props.multiple) {
                const list = [...prev]
                return [...list, file]
            }
            return [file]
        })
    }
    function closeCrop() {
        setCropImage(null)
        props.onAction({
            type: 'CLOSE_CROP'
        })
    }
    function openCrop(file: UploadFile) {
        setCropImage(file)
        props.onAction({
            type: 'OPEN_CROP'
        })
    }
    const onCancelCrop = () => {
        setFileList(prev => {
            if (prev?.length) {
                prev.pop()
                return [...prev]
            }
            return undefined
        })
        closeCrop()
    }

    const handlePreview = async (file: UploadFile) => {
        if (isImage(file)) {
            if (!file.url && !file.preview) {
                file.preview = await getBase64(file.originFileObj as RcFile);
            }

            setPreviewImage(file.url || (file.preview as string));
            setPreviewOpen(true);
            setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
        } else if (file.url) {
            var anchor = document.createElement('a');
            anchor.setAttribute('href', file.url);
            anchor.setAttribute('download', file.name);
            anchor.innerHTML = 'Test.json'
            document.body.appendChild(anchor);
            anchor.click();
            if (anchor.parentNode) anchor.parentNode.removeChild(anchor);
        }
    };
    const onReset = () => {
        setFileList(undefined)
    }
    const beforeUpload = (file: RcFile, fileList: RcFile[] = []) => {
        // const checkCheckedFile = !!fileList.find(rcfile => (rcfile as any).checkSize === false)
        const checkSize = maxSize ? file.size <= maxSize : true;
        if (!checkSize) {
            (file as any).checkSize = false
            setSizeError(prev => {
                return [
                    ...(prev || []),
                    translate(TRANSLATION_CODES.MAX_SIZE_EXCEEDED, { filename: file.name, size: sizeLabel })
                ]
            })
        } else {
            if (!fileList.find(rcfile => (rcfile as any).checkSize === false)) {
                setSizeError(undefined)
            }
        }
        return checkSize || Upload.LIST_IGNORE;
    };
    return (
        <Fragment>
            {
                ((!props.value || props.value.length === 0) || (props.value?.length && !!initialValue?.length)) && (
                    <FormItem
                        fieldProps={props}
                        initialValue={initialValue}
                        onReset={onReset}
                    >
                        {
                            cropImage && props.cropRenderMode === 'replace' ? (
                                <JCropImage file={cropImage} onSave={onSaveCrop} onCancel={onCancelCrop} />
                            ) : (
                                <Fragment>
                                    <Upload.Dragger
                                        beforeUpload={beforeUpload}
                                        onPreview={handlePreview}
                                        showUploadList={props.showPreview}
                                        customRequest={() => { }}
                                        accept={props.accept}
                                        listType="picture-card"
                                        multiple={props.multiple}
                                        fileList={fileList}
                                        onChange={onChange}
                                        maxCount={props.multiple ? props.maxCount : 1}
                                    >
                                        {
                                            !props.minimal && (
                                                <Fragment>
                                                    <p className="ant-upload-drag-icon">
                                                        {
                                                            props.icon ? (
                                                                props.icon
                                                            ) : (
                                                                <UploadOutlined color='primary' aria-hidden={true} />
                                                            )
                                                        }
                                                    </p>
                                                    {
                                                        !props.minimal && !props.showLabel && (
                                                            <p className="ant-upload-hint">
                                                                {props.label}
                                                            </p>
                                                        )
                                                    }
                                                </Fragment>
                                            )
                                        }
                                        <p className="ant-upload-hint">
                                            {translate(TRANSLATION_CODES.UPLOAD_DESCRIPTION)}
                                        </p>
                                    </Upload.Dragger>
                                    {
                                        !!maxSize && !sizeError?.length && (
                                            <Typography.Text type='secondary' className='field-help-message'>{sizeMessage}</Typography.Text>
                                        )
                                    }
                                    {
                                        !!maxSize && !!sizeError?.length && sizeError.map((message, i) => (
                                            <Typography.Text
                                                className='field-help-message'
                                                type='danger'
                                                key={i}
                                                style={{ width: '100%', display: 'block' }}
                                            >
                                                {message}
                                            </Typography.Text>
                                        ))
                                    }

                                </Fragment>
                            )
                        }
                    </FormItem>
                )
            }
            <Modal
                className=""
                open={previewOpen}
                title={previewTitle}
                footer={null}
                onCancel={() => setPreviewOpen(false)}
            >
                <a href={previewImage} download={previewTitle}>
                    <img aria-hidden={true} style={{ width: '100%' }} src={previewImage} />
                </a>
            </Modal>
            {
                cropImage && props.cropRenderMode === 'modal' && (
                    <Modal
                        className='form-modal'
                        maskClosable={false}
                        open={!!cropImage}
                        title={props.label}
                        onCancel={onCancelCrop}
                        footer={null}
                    >
                        <div>
                            <JCropImage file={cropImage} onSave={onSaveCrop} onCancel={onCancelCrop} />
                        </div>
                    </Modal>
                )
            }
        </Fragment>
    )
}